import { useCallback } from 'react';
import type { GuestSignupTrigger } from '../context/GuestSignupModalContext';
import { ensureAnonSessionId } from '@/shared/utils/anonymousSession';
export { ensureAnonSessionId };

/**
 * LocalStorage carrier for the pre-login signup flow.
 *
 * The fallback path is:
 *   1. User submits Screen 1 of AccountCreationModal
 *   2. We store the captured form fields + trigger context here
 *   3. We hand off to Kinde (email pre-filled, OTP)
 *   4. After Kinde callback, AuthCallback reads this back, forwards `signupAttribution`
 *      to POST /auth/complete-registration, navigates to `deepLinkPath`, then clears.
 *
 * Survives the page navigation to Kinde and back. Cleared on success or on
 * an explicit reset (e.g. user closes the modal).
 */

const STORAGE_KEY = 'bquest_signup_context';

/**
 * Screen 2 answers (FP identification). Captured pre-Kinde, applied post-Kinde
 * via PATCH /auth/care-seeker/me/fp-association.
 *
 * When `fpAnswer === 'no'`, the other fields are absent. When `fpAnswer === 'yes'`,
 * either `partnerCompanyId + advisorId` are set, or `partnerCompanyId + unmatchedAdvisorName`,
 * or just `unmatchedAdvisorName` (if "Advisor not listed" was chosen at the company step).
 */
export type SignupScreen2 = {
  fpAnswer: 'yes' | 'no';
  partnerCompanyId?: string;
  advisorId?: string;
  unmatchedAdvisorName?: string;
};

export type SignupContext = {
  /** Optional — modal no longer asks for name (Q1 decision: removed because Kinde doesn't pre-fill these fields and asking twice is bad UX). Kept optional so old cached contexts still parse. AuthCallback falls back to Kinde JWT claims for the name. */
  firstName?: string;
  lastName?: string;
  email: string;
  termsAcceptedAt: string; // ISO 8601
  trigger: GuestSignupTrigger;
  triggerEntityId?: string;
  anonSessionId: string;
  /** Path to navigate to after successful Kinde callback (deep link). */
  deepLinkPath?: string;
  /** Referral code from URL params (resolved via useUnauthAttribution). Spec Item 5. */
  signupReferralCode?: string;
  /** Screen 2 answers (spec Item 3). Applied via PATCH after Kinde callback. */
  screen2?: SignupScreen2;
  /** Invite token from URL (spec Item 6). Forwarded to /complete-registration so the
   *  backend calls handleInviteAcceptance and creates the inviter↔invitee network row. */
  inviteId?: string;
  /** Navigator session UUID of a care plan built while anonymous. Carried across the
   *  Kinde redirect so AuthCallback can claim the guest plan for the new account
   *  (POST /navigator/claim-guest-plan) and drop the user back on their plan. */
  navigatorSessionId?: string;
};

const readRaw = (): unknown => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const isSignupContext = (value: unknown): value is SignupContext => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.email === 'string'
    && typeof v.termsAcceptedAt === 'string'
    && typeof v.trigger === 'string'
    && typeof v.anonSessionId === 'string'
  );
};

export const useSignupContext = () => {
  const save = useCallback((ctx: SignupContext) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
    } catch {
      // Storage unavailable — caller will lose deep-link / attribution context
      // but the Kinde signup itself still proceeds.
    }
  }, []);

  const read = useCallback((): SignupContext | null => {
    const raw = readRaw();
    return isSignupContext(raw) ? raw : null;
  }, []);

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return { save, read, clear, ensureAnonSessionId };
};
